import { Box, Button, Form } from '@factorialco/deprecated-design-system'
import { CompetenciesDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  CompetenciesBulkAddToGroupDocument,
  CompetenciesBulkAddToGroupMutationVariables,
  CompetenciesCompetencyGroupsDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CompetencyGroupSelector } from 'modules/competencies/components/CompetencyGroupSelector'

interface Props {
  selectedCompetencies: string[]
  onClose: () => void
}

export const AddToGroupModal: React.FC<Props> = ({ selectedCompetencies, onClose }) => {
  const { data, loading: groupsLoading } = useQuery(CompetenciesCompetencyGroupsDocument)
  const [bulkAddToGroupCompetenciesCompetency, { loading: addingToGroup }] = useMutation(
    CompetenciesBulkAddToGroupDocument,
    {
      extractorFn: (addToGroupData) =>
        addToGroupData.competenciesMutations.bulkAddToGroupCompetenciesCompetency,
      refetchQueries: [CompetenciesDataTableDocument],
    }
  )

  const form = useForm<CompetenciesBulkAddToGroupMutationVariables>({
    defaultValues: {
      ids: selectedCompetencies,
      competencyGroupId: '',
    },
    validate: (formData) => {
      const errors: FormErrors<CompetenciesBulkAddToGroupMutationVariables> = {}

      if (!formData.competencyGroupId) {
        errors.competencyGroupId = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(submitData, { setErrors }) {
      const result = await bulkAddToGroupCompetenciesCompetency({
        variables: {
          ids: submitData.ids,
          competencyGroupId: submitData.competencyGroupId,
        },
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        track(trackingEvents.competencies.assignCompetenciesToGroup)
      }

      onClose()

      return handled
    },
  })

  return (
    <Modal
      illustration="new-folder"
      title={i18n.t('competencies_v3.competencies_table.bulk_actions.add_to_group.dialog.title')}
      description={i18n.t(
        'competencies_v3.competencies_table.bulk_actions.add_to_group.dialog.description'
      )}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button disabled={!form.values.competencyGroupId || groupsLoading || addingToGroup} submit>
          {i18n.t('common.confirm')}
        </Button>
      }
    >
      <Box gap="s16">
        {!groupsLoading && data && (
          <Form onSubmit={form.handleSubmit} padding={null}>
            <CompetencyGroupSelector
              value={form.values.competencyGroupId}
              onChange={(groupId) => form.setFieldValue('competencyGroupId', groupId ?? '')}
              error={form.errors.competencyGroupId?.toString()}
            />
          </Form>
        )}
      </Box>
    </Modal>
  )
}
